import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Flag } from '@neo/primitives/icon'
import type { SelectOption } from '@neo/primitives/select'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { identity, pickBy } from 'lodash'
import { TableContainer } from 'v2/shared/components/table-container'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import { ConvChannels } from 'v2/types/api/conversations'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { generateToastsForMutation } from 'v2/utils/toasts'
import { useAccountNumbersQuery } from '../phone-numbers/queries'
import { useChannelConfigurationMutation, useChannelConfigurationQuery } from '../queries'
import { getWAChannelFields, WAChannelDefaultValues } from './channel.config'

export const WhatsAppChannel = () => {
  const { data: formData } = useChannelConfigurationQuery(ConvChannels.WHATSAPP)
  const { data: callerIds } = useAccountNumbersQuery('simple', ConvChannels.WHATSAPP, true)
  const { mutate, isPending } = useChannelConfigurationMutation(ConvChannels.WHATSAPP)
  const { hasPermission } = usePermissions()

  // Options for the select dropdowns
  const options = callerIds?.map<SelectOption>((callerId) => {
    const number = parsePhoneNumberFromString(callerId.number, 'US')
    return {
      label: (
        <div className="flex items-center gap-2">
          <Flag iso={number?.country || ''} circle size={16} />
          <span className="font-jetbrains text-xs-400 tabular-nums">
            {number?.formatInternational()}
          </span>
          {callerId.name_tag && (
            <span className="text-secondary truncate">({callerId.name_tag})</span>
          )}
        </div>
      ),
      searchValue: `${callerId.name_tag} ${callerId.number}`,
      value: callerId.id,
    }
  })

  return (
    <TableContainer>
      <GenericForm<typeof WAChannelDefaultValues>
        defaultValues={formData || WAChannelDefaultValues}
        id="whatsapp-channel-settings"
        isSubmitting={isPending}
        submitText={isPending ? 'Saving...' : 'Save'}
        includeSubmitButton={hasPermission(RbacModules.channelWhatsapp, RbacPermissionTypes.create)}
        className="gap-4"
        fields={
          getWAChannelFields(options || []) as GenericFieldConfig<typeof WAChannelDefaultValues>
        }
        onSubmit={(values) =>
          mutate(
            pickBy(values, identity) as typeof WAChannelDefaultValues,
            generateToastsForMutation(
              { title: 'Success', description: 'WhatsApp settings updated successfully' },
              { title: 'Error', description: 'Failed to update WhatsApp settings' },
            ),
          )
        }
      />
    </TableContainer>
  )
}
